import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import styled from "styled-components";
import crypto from "crypto-browserify";
import GET_PROFILE from "@sellout/models/.dist/graphql/queries/profile.query";
import CONNECT_HIVE from "@sellout/models/.dist/graphql/mutations/connectHive.mutation";
import { Flex, Loader, LoaderSizes, Colors, Icon, Icons } from "@sellout/ui";
import Button, { ButtonTypes } from "@sellout/ui/build/components/Button";
import * as AppActions from "../redux/actions/app.actions";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import { useDispatch } from "react-redux";
import {
    HIVE_AUTHORIZATION_URL,
    HIVE_REDIRECT_URI,
    HIVE_SCOPE,
} from "../env";

const ConnectButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 10px;
  background-color: #FF6B00;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  width: fit-content;
  transition: background-color 0.2s;
  &:hover {
    background-color: #E55D00;
  }
`;

const ConnectedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConnectedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConnectedText = styled.span`
  color: ${Colors.Green};
  font-weight: 600;
  font-size: 1.4rem;
`;

// Generate cryptographically random base64url string
function randomString(byteLength: number): string {
    const bytes = crypto.randomBytes(byteLength);
    return bytes
        .toString("base64")
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

// Create PKCE code challenge from verifier
function createCodeChallenge(codeVerifier: string): string {
    return crypto
        .createHash("sha256")
        .update(codeVerifier)
        .digest("base64")
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

type ConnectHiveButtonProps = {
    isConnected: boolean;
    clientId?: string;
    clientSecret?: string;
};

const ConnectHiveButton: React.FC<ConnectHiveButtonProps> = ({
    isConnected,
    clientId,
    clientSecret,
}) => {
    const dispatch = useDispatch();
    const [connecting, setConnecting] = useState(false);

    const [connectHive, { loading }] = useMutation(CONNECT_HIVE, {
        refetchQueries: [{ query: GET_PROFILE }],
        awaitRefetchQueries: true,
        onCompleted(data) {
            if (data?.connectHive) {
                setConnecting(false);
                // Clean up session storage
                sessionStorage.removeItem("hive_oauth_state");
                sessionStorage.removeItem("hive_code_verifier");
                // Remove code/state from URL without page reload
                window.history.replaceState(
                    {},
                    document.title,
                    window.location.pathname
                );
                dispatch(
                    AppActions.showNotification(
                        "Hive integration connected successfully!",
                        AppNotificationTypeEnum.Success
                    )
                );
            }
        },
        onError(error) {
            setConnecting(false);
            sessionStorage.removeItem("hive_oauth_state");
            sessionStorage.removeItem("hive_code_verifier");
            dispatch(
                AppActions.showNotification(
                    error.message || "Failed to connect to Hive",
                    AppNotificationTypeEnum.Error
                )
            );
        },
    });

    // Listen for messages from the OAuth popup
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== "HIVE_OAUTH_CALLBACK") return;

            const { code, state, error, error_description } = event.data.payload;

            if (error) {
                dispatch(
                    AppActions.showNotification(
                        error_description || error,
                        AppNotificationTypeEnum.Error
                    )
                );
                return;
            }

            if (code && state && !loading && !isConnected) {
                const savedState = sessionStorage.getItem("hive_oauth_state");
                const savedVerifier = sessionStorage.getItem("hive_code_verifier");

                if (!savedState || state !== savedState) {
                    dispatch(
                        AppActions.showNotification(
                            "Hive OAuth state mismatch. Please try connecting again.",
                            AppNotificationTypeEnum.Error
                        )
                    );
                    return;
                }

                if (!savedVerifier) {
                    dispatch(
                        AppActions.showNotification(
                            "Missing PKCE verifier. Please try connecting again.",
                            AppNotificationTypeEnum.Error
                        )
                    );
                    return;
                }

                setConnecting(true);
                connectHive({
                    variables: {
                        code,
                        codeVerifier: savedVerifier,
                        clientId,
                        clientSecret,
                    },
                });
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [loading, isConnected, connectHive, dispatch, clientId, clientSecret]);

    const startOAuthFlow = () => {
        if (loading || connecting) return;

        if (!clientId || !clientSecret) {
            dispatch(
                AppActions.showNotification(
                    "Please save a Hive Client ID and Client Secret before connecting.",
                    AppNotificationTypeEnum.Error
                )
            );
            return;
        }

        // Generate PKCE values
        const codeVerifier = randomString(64);
        const codeChallenge = createCodeChallenge(codeVerifier);
        const state = randomString(32);

        // Save to sessionStorage for callback verification
        sessionStorage.setItem("hive_oauth_state", state);
        sessionStorage.setItem("hive_code_verifier", codeVerifier);

        // Build authorization URL
        const params = new URLSearchParams({
            response_type: "code",
            client_id: clientId,
            redirect_uri: HIVE_REDIRECT_URI,
            scope: HIVE_SCOPE,
            state,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
        });

        const authUrl = `${HIVE_AUTHORIZATION_URL}?${params.toString()}`;

        // Setup popup dimensions and center on screen
        const width = 500;
        const height = 650;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        window.open(
            authUrl,
            "HiveOAuth",
            `width=${width},height=${height},left=${left},top=${top},status=yes,scrollbars=yes`
        );
    };

    if (isConnected) {
        return (
            <ConnectedContainer>
                <ConnectedRow>
                    <Icon
                        icon={Icons.CheckCircle}
                        color={Colors.Green}
                        size={14}
                    />
                    <ConnectedText>Hive account connected</ConnectedText>
                </ConnectedRow>
                <Button
                    type={ButtonTypes.Regular}
                    text="OPEN HIVE DASHBOARD"
                    margin="0px"
                    onClick={() =>
                        window.open("https://app.hive.co/", "_blank")
                    }
                />
            </ConnectedContainer>
        );
    }

    return (
        <Flex align="center">
            <ConnectButton onClick={startOAuthFlow}>
                {loading || connecting ? "Connecting..." : "Connect to Hive"}
            </ConnectButton>
            {(loading || connecting) && (
                <>
                    <div style={{ width: 15 }} />
                    <Loader size={LoaderSizes.Small} color={Colors.Orange} />
                </>
            )}
        </Flex>
    );
};

export default ConnectHiveButton;
